import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useApiMutation } from "../../hooks";
import type { CheckSchema, CheckValidation, HttpMethod } from "../../types";
import type { HarEntry } from "../har-viewer";
import { TestSelect } from "../test-select";

// Auth header patterns to auto-select
const AUTH_HEADER_PATTERNS = [
  /^authorization$/i,
  /^x-api-key$/i,
  /^x-auth-/i,
  /^x-token/i,
  /^x-access-token$/i,
  /^api-key$/i,
  /^bearer$/i,
];

// Headers to exclude from selection (browser/infra headers)
const EXCLUDED_HEADERS = new Set([
  "host",
  "connection",
  "accept-encoding",
  "accept-language",
  "sec-fetch-dest",
  "sec-fetch-mode",
  "sec-fetch-site",
  "sec-ch-ua",
  "sec-ch-ua-mobile",
  "sec-ch-ua-platform",
  "upgrade-insecure-requests",
  "cache-control",
  "pragma",
  "user-agent",
  "referer",
  "origin",
  "cookie",
]);

function isAuthHeader(name: string): boolean {
  return AUTH_HEADER_PATTERNS.some((pattern) => pattern.test(name));
}

function generateCheckName(entry: HarEntry): string {
  const url = new URL(entry.request.url);
  const pathParts = url.pathname.split("/").filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1] || "endpoint";
  return `${entry.request.method} ${lastPart}`;
}

export interface CreateCheckFromHarPanelProps {
  entry: HarEntry;
  testId?: string;
  testRunId?: string;
  appConfigId?: string;
  onSuccess?: (checkId: string) => void;
  onClose?: () => void;
}

export const CreateCheckFromHarPanel: React.FC<CreateCheckFromHarPanelProps> = ({
  entry,
  testId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  testRunId: _testRunId,
  appConfigId,
  onSuccess,
  onClose,
}) => {
  // _testRunId is available for future use (tracking source of check creation)
  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();

  // Filter headers to show (exclude browser/infra headers)
  // Sort with auth headers first, then content-type, then the rest alphabetically
  const availableHeaders = useMemo(() => {
    const filtered = entry.request.headers.filter(
      (h) => !h.name.startsWith(":") && !EXCLUDED_HEADERS.has(h.name.toLowerCase()),
    );
    return filtered.sort((a, b) => {
      const aIsAuth = isAuthHeader(a.name);
      const bIsAuth = isAuthHeader(b.name);
      const aIsContentType = a.name.toLowerCase() === "content-type";
      const bIsContentType = b.name.toLowerCase() === "content-type";

      // Auth headers first
      if (aIsAuth && !bIsAuth) return -1;
      if (!aIsAuth && bIsAuth) return 1;
      // Then content-type
      if (aIsContentType && !bIsContentType) return -1;
      if (!aIsContentType && bIsContentType) return 1;
      // Then alphabetically
      return a.name.localeCompare(b.name);
    });
  }, [entry]);

  // State for form
  const [name, setName] = useState(() => generateCheckName(entry));
  const [description, setDescription] = useState("");
  const [selectedHeaders, setSelectedHeaders] = useState<Set<string>>(() => {
    // Pre-select auth headers
    const authHeaders = new Set<string>();
    availableHeaders.forEach((h) => {
      if (isAuthHeader(h.name)) {
        authHeaders.add(h.name);
      }
    });
    // Also include content-type if present
    availableHeaders.forEach((h) => {
      if (h.name.toLowerCase() === "content-type") {
        authHeaders.add(h.name);
      }
    });
    return authHeaders;
  });
  const [testDependencyId, setTestDependencyId] = useState<string | undefined>(testId);
  const [cacheTtlSeconds, setCacheTtlSeconds] = useState<number | undefined>();
  const [expectedStatusCode, setExpectedStatusCode] = useState<number>(entry.response.status);

  // Create check mutation
  const { mutate: createCheck, isPending } = useApiMutation<CheckSchema, { id: string }>(
    "POST",
    "/checks",
    {
      onSuccess: (data) => {
        enqueueSnackbar("Check created successfully", { variant: "success" });
        if (onSuccess) {
          onSuccess(data.id);
        } else {
          nav(`/checks/${data.id}`);
        }
        onClose?.();
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to create check: ${error.message}`, {
          variant: "error",
        });
      },
    },
  );

  const handleHeaderToggle = (headerName: string) => {
    setSelectedHeaders((prev) => {
      const next = new Set(prev);
      if (next.has(headerName)) {
        next.delete(headerName);
      } else {
        next.add(headerName);
      }
      return next;
    });
  };

  const handleSelectAllHeaders = () => {
    setSelectedHeaders(new Set(availableHeaders.map((h) => h.name)));
  };

  const handleDeselectAllHeaders = () => {
    setSelectedHeaders(new Set());
  };

  const handleCreate = () => {
    if (!appConfigId) {
      enqueueSnackbar("App configuration is required", { variant: "error" });
      return;
    }

    // Build headers object from selected headers
    const headers: Record<string, string> = {};
    availableHeaders.forEach((h) => {
      if (selectedHeaders.has(h.name)) {
        headers[h.name] = h.value;
      }
    });

    // Build validation
    const validation: CheckValidation = {
      expected_status_code: expectedStatusCode,
      ai_mode: "fast",
    };

    const checkData: CheckSchema = {
      name,
      description: description || undefined,
      app_config_id: appConfigId,
      check_type: "api",
      config: {
        url: entry.request.url,
        method: entry.request.method as HttpMethod,
        headers: Object.keys(headers).length > 0 ? headers : undefined,
        body: entry.request.postData?.text || undefined,
        follow_redirects: true,
      },
      validation,
      variables: [],
      retry_strategy: "none",
      max_retries: 3,
      retry_delay_seconds: 1,
      backoff_multiplier: 2,
      timeout_ms: 30000,
      test_dependency_id: testDependencyId || undefined,
      dependency_cache_ttl_seconds: cacheTtlSeconds || undefined,
    };

    createCheck(checkData);
  };

  // Update name when entry changes
  useEffect(() => {
    setName(generateCheckName(entry));
    setExpectedStatusCode(entry.response.status);
  }, [entry]);

  const isValid = name && appConfigId;

  return (
    <Box sx={{ p: 0 }}>
      <Stack spacing={3}>
        {/* Request Summary */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Creating check from:
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={entry.request.method} color="primary" size="small" />
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                wordBreak: "break-all",
              }}
            >
              {entry.request.url}
            </Typography>
          </Stack>
        </Box>

        <Divider />

        {/* Basic Info */}
        <TextField
          required
          label="Check Name"
          size="small"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="A descriptive name for this check"
        />

        <TextField
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          maxRows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText="Optional description of what this check monitors"
        />

        {/* App Config Warning */}
        {!appConfigId && (
          <Alert severity="warning">
            No app configuration available. Please ensure this HAR viewer is opened from a test run
            with an app configuration.
          </Alert>
        )}

        <Divider />

        {/* Header Selection */}
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">
              Request Headers ({selectedHeaders.size} selected)
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={handleSelectAllHeaders}>
                Select All
              </Button>
              <Button size="small" onClick={handleDeselectAllHeaders}>
                Clear
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              maxHeight: 200,
              overflow: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
            }}
          >
            {availableHeaders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No headers to select
              </Typography>
            ) : (
              availableHeaders.map((header) => (
                <FormControlLabel
                  key={header.name}
                  control={
                    <Checkbox
                      checked={selectedHeaders.has(header.name)}
                      onChange={() => handleHeaderToggle(header.name)}
                      size="small"
                    />
                  }
                  label={
                    <Stack direction="column" spacing={0}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          {header.name}
                        </Typography>
                        {isAuthHeader(header.name) && (
                          <Chip
                            label="Auth"
                            size="small"
                            color="warning"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.65rem",
                          color: "text.secondary",
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {header.value.length > 50
                          ? `${header.value.substring(0, 50)}...`
                          : header.value}
                      </Typography>
                    </Stack>
                  }
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1,
                    width: "100%",
                  }}
                />
              ))
            )}
          </Box>
          <FormHelperText>
            Auth headers are auto-selected. Headers will be included in the check request.
          </FormHelperText>
        </Box>

        <Divider />

        {/* Test Dependency */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Test Dependency (Optional)
          </Typography>
          <TestSelect
            label="Run test first for auth tokens"
            placeholder="Search for a test..."
            value={testDependencyId || null}
            onChange={(testId) => setTestDependencyId(testId || undefined)}
            appConfigId={appConfigId}
            size="small"
            helperText="Select a test to run before this check to generate fresh auth tokens"
          />

          {testDependencyId && (
            <TextField
              label="Cache TTL (seconds)"
              size="small"
              fullWidth
              type="number"
              value={cacheTtlSeconds || ""}
              onChange={(e) =>
                setCacheTtlSeconds(e.target.value ? parseInt(e.target.value, 10) : undefined)
              }
              InputProps={{
                endAdornment: <InputAdornment position="end">sec</InputAdornment>,
              }}
              helperText="Leave empty to auto-detect from JWT expiration"
              sx={{ mt: 2 }}
            />
          )}
        </Box>

        <Divider />

        {/* Validation */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Validation
          </Typography>
          <TextField
            label="Expected Status Code"
            size="small"
            fullWidth
            type="number"
            value={expectedStatusCode}
            onChange={(e) => setExpectedStatusCode(parseInt(e.target.value, 10) || 200)}
            helperText="The HTTP status code expected from this endpoint"
          />
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {onClose && (
            <Button onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
          )}
          <Button variant="contained" onClick={handleCreate} disabled={!isValid || isPending}>
            {isPending ? "Creating..." : "Create Check"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CreateCheckFromHarPanel;
